<template>
  <div class="min-h-screen agent-finder p-6">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b">
      <div class="mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <img
              src="https://uptodatewebdesign.s3.eu-west-3.amazonaws.com/uploads/icon-1741669842731.png"
              alt="Selectair"
              class="w-12 h-12 object-contain"
            />
            <div>
              <h1 class="text-2xl font-bold text-gray-900">
                Vind je Selectair reisagent
              </h1>
              <p class="text-gray-600 mt-1">Zoek een reisagent bij jou in de buurt</p>
            </div>
          </div>
          
          <!-- <div class="flex items-center gap-4">
            <router-link
              to="/"
              class="flex items-center gap-2 px-4 py-2 text-theme border border-theme rounded-lg hover:bg-theme hover:text-white transition-colors"
            >
              <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
              </svg>
              <span class="hidden sm:block">Terug naar vakanties</span>
            </router-link>
          </div> -->
        </div>
      </div>
    </header>

    <div class="mx-auto">
      <!-- Search Section -->
      <div class="bg-white rounded-xl shadow-sm p-6 mb-8">
        <h2 class="text-xl font-semibold text-gray-900 mb-6">Zoek reisagenten</h2>
        
        <div class="space-y-6 mb-6" style="max-width: 480px;">
          <!-- Location Method -->
          <div>
            <button
              @click="requestLocation"
              :disabled="loadingLocation"
              class="w-full flex items-center justify-center gap-2 px-4 py-3 bg-theme text-white rounded-lg hover:bg-theme-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              <svg v-if="!loadingLocation" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              {{ loadingLocation ? 'Locatie ophalen...' : 'Gebruik mijn locatie' }}
            </button>
            
            <div v-if="locationError" class="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div class="flex items-start gap-2">
                <svg class="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L5.232 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p class="text-sm text-yellow-800">{{ locationError }}</p>
              </div>
            </div>
          </div>

          <!-- Zip Code Method -->
          <div class="flex gap-4">
            <input
              v-model="zipCode"
              @keyup.enter="searchByZipCode"
              type="text"
              placeholder="Postcode (bijv. 3500)"
              class="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme focus:border-theme transition-colors"
            />
            <button
              @click="searchByZipCode"
              :disabled="!zipCode.trim() || loadingAgents"
              class="px-6 py-3 border border-theme text-theme bg-white rounded-lg hover:bg-theme hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Zoek
            </button>
          </div>
        </div>

        <!-- Range Selector -->
        <div v-if="userLocation || zipCode" class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">Zoekafstand</label>
          <select
            v-model="searchRange"
            @change="searchAgents"
            class="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme"
          >
            <option value="5000">5 km</option>
            <option value="10000">10 km</option>
            <option value="25000">25 km</option>
            <option value="50000">50 km</option>
            <option value="100000">100 km</option>
          </select>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loadingAgents" class="flex items-center justify-center py-12">
        <div class="text-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-theme mx-auto mb-4"></div>
          <p class="text-gray-600">Reisagenten zoeken...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <svg class="mx-auto h-12 w-12 text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L5.232 15.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <h3 class="text-lg font-medium text-red-900 mb-2">Er is een fout opgetreden</h3>
        <p class="text-red-700 mb-4">{{ error }}</p>
        <button
          @click="retrySearch"
          class="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Opnieuw proberen
        </button>
      </div>

      <!-- Results -->
      <div v-else-if="agents.length > 0" class="space-y-6">
        <!-- Results Header -->
        <div class="bg-white rounded-lg shadow-sm p-6">
          <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <h2 class="text-xl font-semibold text-gray-900">
                {{ agents.length }} reisagenten gevonden
              </h2>
              <p class="text-gray-600 mt-1">
                Binnen {{ searchRange / 1000 }} km van {{ searchLocation }}
              </p>
            </div>
            
            <!-- View Toggle -->
            <div class="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                @click="viewMode = 'list'"
                :class="[
                  'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                  viewMode === 'list' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                ]"
              >
                <svg class="h-4 w-4 inline mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                Lijst
              </button>
              <button
                @click="viewMode = 'map'"
                :class="[
                  'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                  viewMode === 'map' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                ]"
              >
                <svg class="h-4 w-4 inline mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                Kaart
              </button>
            </div>
          </div>
        </div>

        <!-- Map View -->
        <div v-if="viewMode === 'map'" class="bg-white rounded-lg shadow-sm overflow-hidden" style="height: 70vh;">
          <div class="flex flex-col lg:flex-row h-full">
            <!-- Agents List Sidebar -->
            <div class="w-full lg:w-1/2 lg:overflow-y-auto border-b lg:border-b-0 lg:border-r max-h-60 lg:max-h-none overflow-y-auto lg:overflow-y-auto">
              <div class="p-4 space-y-4">
                <div
                  v-for="agent in agents"
                  :key="agent.id"
                  @mouseenter="highlightAgent(agent.id)"
                  @mouseleave="unhighlightAgent()"
                  @click="centerMapOnAgent(agent)"
                  :class="[
                    'bg-white border rounded-lg p-4 cursor-pointer transition-all duration-200',
                    highlightedAgent === agent.id 
                      ? 'border-theme shadow-md transform scale-[1.02]' 
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  ]"
                >
                  <div class="flex gap-4">
                    <div class="relative w-14 h-14 agent-image-container rounded-lg overflow-hidden flex-shrink-0">
                      <!-- Blurred background image -->
                      <img
                        :src="agent.avatar"
                        alt=""
                        class="absolute inset-0 w-full h-full object-cover agent-image-blur"
                        @error="handleImageError"
                      />
                      <!-- Main image -->
                      <img
                        :src="agent.avatar"
                        :alt="agent.name"
                        class="relative w-full h-full object-contain z-10 agent-image-main"
                        @error="handleImageError"
                      />
                      <!-- Distance badge on image -->
                      <div class="absolute -top-1 -right-1 bg-theme text-white text-xs px-1.5 py-0.5 rounded-full font-medium z-20">
                        {{ formatDistance(agent.distance).replace(' km', '') }}
                      </div>
                    </div>
                    
                    <div class="flex-1 min-w-0">
                      <h3 class="text-sm font-semibold text-gray-900 truncate mb-1">{{ agent.name }}</h3>
                      <div class="text-xs text-gray-600 space-y-0.5">
                        <p class="font-medium text-gray-700 truncate">{{ agent.addressLine1 }}</p>
                        <p class="text-gray-500 truncate">{{ agent.addressLine2 }}</p>
                      </div>
                      
                      <!-- Contact info on separate line -->
                      <div class="flex items-center gap-4 mt-2 text-xs">
                        <a
                          v-if="agent.phone"
                          :href="`tel:${agent.phone}`"
                          @click.stop
                          class="flex items-center gap-1 text-theme hover:text-theme-hover font-medium"
                        >
                          <svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          Bel
                        </a>
                        <a
                          v-if="agent.website"
                          :href="`https://${agent.website}`"
                          target="_blank"
                          @click.stop
                          class="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
                        >
                          <svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Site
                        </a>
                      </div>
                      
                      <!-- Select button -->
                      <!-- <button
                        @click.stop="selectAgent(agent)"
                        class="mt-3 px-4 py-1.5 bg-theme text-white rounded-md text-xs font-medium hover:bg-theme-hover transition-all duration-200 shadow-sm hover:shadow-md active:transform active:scale-95 self-start"
                      >
                        Selecteer reisagent
                      </button> -->
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Map Container -->
            <div class="w-full lg:w-1/2 relative flex-1">
              <div id="agents-map" class="w-full h-full"></div>
              
              <!-- Map loading state -->
              <div v-if="!mapLoaded" class="absolute inset-0 flex items-center justify-center bg-gray-100">
                <div class="text-center">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-theme mx-auto mb-2"></div>
                  <p class="text-sm text-gray-600">Kaart laden...</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- List View (Original Cards) -->
        <div v-else class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="agent in agents"
            :key="agent.id"
            class="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
          >
            <!-- Agent Image with blurred background -->
            <div class="h-56 agent-image-container overflow-hidden relative">
              <!-- Blurred background image -->
              <img
                :src="agent.avatar"
                alt=""
                class="absolute inset-0 w-full h-full object-cover agent-image-blur"
                @error="handleImageError"
              />
              <!-- Main image -->
              <img
                :src="agent.avatar"
                :alt="agent.name"
                class="relative w-full h-full object-contain z-10 agent-image-main"
                @error="handleImageError"
              />
              <!-- Distance badge -->
              <div class="absolute top-3 right-3 bg-white bg-opacity-90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-medium text-theme z-20">
                {{ formatDistance(agent.distance) }}
              </div>
            </div>

            <!-- Agent Info -->
            <div class="p-5">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ agent.name }}</h3>
              
              <div class="space-y-2 text-sm text-gray-600 mb-5">
                <div class="flex items-start gap-2">
                  <svg class="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div class="min-w-0 flex-1">
                    <div class="font-medium text-gray-700">{{ agent.addressLine1 }}</div>
                    <div class="text-gray-500">{{ agent.addressLine2 }}</div>
                  </div>
                </div>

                <div v-if="agent.phone" class="flex items-center gap-2">
                  <svg class="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <a :href="`tel:${agent.phone}`" class="hover:text-theme transition-colors font-medium">
                    {{ agent.phone }}
                  </a>
                </div>

                <div v-if="agent.email" class="flex items-center gap-2">
                  <svg class="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <a :href="`mailto:${agent.email}`" class="hover:text-theme transition-colors font-medium truncate">
                    {{ agent.email }}
                  </a>
                </div>
              </div>

              <!-- Action Buttons - Professional compact layout -->
              <div class="flex flex-col gap-4">
                <!-- Primary action -->
                <!-- <button
                  @click="selectAgent(agent)"
                  class="px-6 py-2.5 bg-theme text-white rounded-lg hover:bg-theme-hover transition-all duration-200 font-semibold text-sm shadow-sm hover:shadow-md active:transform active:scale-95 self-start"
                >
                  Selecteer reisagent
                </button> -->
                
                <!-- Quick action buttons in compact row -->
                <div class="flex items-center justify-between">
                  <!-- Contact actions -->
                  <div class="flex gap-2">
                    <a
                      v-if="agent.phone"
                      :href="`tel:${agent.phone}`"
                      class="flex items-center gap-1.5 px-3 py-1.5 text-theme bg-theme-light rounded-md hover:bg-theme hover:text-white transition-colors text-xs font-medium"
                      title="Bel direct"
                    >
                      <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span class="hidden sm:inline">Bel</span>
                    </a>
                    
                    <a
                      v-if="agent.website"
                      :href="`https://${agent.website}`"
                      target="_blank"
                      class="flex items-center gap-1.5 px-3 py-1.5 text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors text-xs font-medium"
                      title="Bezoek website"
                    >
                      <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      <span class="hidden sm:inline">Website</span>
                    </a>
                    
                    <a
                      v-if="agent.email"
                      :href="`mailto:${agent.email}`"
                      class="flex items-center gap-1.5 px-3 py-1.5 text-purple-600 bg-purple-50 rounded-md hover:bg-purple-100 transition-colors text-xs font-medium"
                      title="Stuur email"
                    >
                      <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span class="hidden sm:inline">Email</span>
                    </a>
                  </div>
                  
                  <!-- Map action -->
                  <button
                    @click="showOnMap(agent)"
                    class="flex items-center gap-1.5 px-3 py-1.5 text-theme bg-theme-light rounded-md hover:bg-theme hover:text-white transition-colors text-xs font-medium"
                    title="Toon op kaart"
                  >
                    <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span class="hidden sm:inline">Kaart</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Results -->
      <div v-else-if="searchPerformed && !loadingAgents" class="bg-white rounded-lg shadow-sm p-12 text-center">
        <svg class="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <h3 class="text-xl font-medium text-gray-900 mb-2">Geen reisagenten gevonden</h3>
        <p class="text-gray-600 mb-6">
          Probeer een grotere zoekafstand of een andere locatie.
        </p>
        <button
          @click="expandSearch"
          class="px-6 py-3 bg-theme text-white rounded-md hover:bg-theme-hover transition-colors"
        >
          Uitbreiden naar 100 km
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgentFinder',
  data() {
    return {
      // Search state
      zipCode: '',
      userLocation: null,
      searchRange: '25000',
      searchLocation: '',
      
      // Loading and error states
      loadingLocation: false,
      loadingAgents: false,
      locationError: null,
      error: null,
      
      // Results
      agents: [],
      searchPerformed: false,
      
      // Map state
      viewMode: 'list', // 'list' or 'map'
      map: null,
      mapLoaded: false,
      markers: [],
      highlightedAgent: null,
      mapCenter: null
    }
  },
  
  computed: {
    shopSettings() {
      return this.$root.shopSettings || {}
    }
  },
  
  methods: {
    // Check URL parameters for auto-search
    checkAutoSearch() {
      const shouldAutoSearch = this.$route.query.search === 'true' || 
                              this.$route.query.auto === 'true' || 
                              this.$route.query.location === 'true'

      if (shouldAutoSearch) {
        // Small delay to ensure component is fully mounted
        setTimeout(() => {
          this.requestLocation()
        }, 500)
      }
    },
    
    async requestLocation() {
      this.loadingLocation = true
      this.locationError = null
      
      if (!navigator.geolocation) {
        this.locationError = 'Geolocatie wordt niet ondersteund door uw browser.'
        this.loadingLocation = false
        return
      }
      
      try {
        const position = await this.getCurrentPosition()
        this.userLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        }
        this.searchLocation = `uw huidige locatie`
        await this.searchAgents()
      } catch (error) {
        console.error('Location error:', error)
        this.locationError = 'Kon uw locatie niet ophalen. Gelieve uw locatie te delen in uw browser.'
      } finally {
        this.loadingLocation = false
      }
    },
    
    getCurrentPosition() {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        })
      })
    },
    
    async searchByZipCode() {
      if (!this.zipCode.trim()) return
      
      try {
        // First, get coordinates for the zip code using a geocoding service
        const location = await this.geocodeZipCode(this.zipCode.trim())
        this.userLocation = location
        this.searchLocation = this.zipCode.trim()
        await this.searchAgents()
      } catch (error) {
        console.error('Geocoding error:', error)
        this.error = 'Kon de locatie voor deze postcode niet vinden.'
      }
    },
    
    async geocodeZipCode(zipCode) {
      // For Belgium, we can use a simple approach or integrate with a geocoding service
      // For now, let's use a basic coordinate for the zipcode (this should be enhanced with a real geocoding service)
      
      // This is a basic implementation - in production you'd want to use a proper geocoding service
      // For Belgian postal codes, we can use OpenStreetMap Nominatim or Google Geocoding API
      try {
        const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=1&postalcode=${zipCode}&country=belgium`)
        const data = await response.json()
        
        if (data && data.length > 0) {
          return {
            latitude: parseFloat(data[0].lat),
            longitude: parseFloat(data[0].lon)
          }
        } else {
          throw new Error('Postcode niet gevonden')
        }
      } catch (error) {
        throw new Error('Kon de locatie niet ophalen voor deze postcode')
      }
    },
    
    async searchAgents() {
      if (!this.userLocation) return

      this.loadingAgents = true
      this.error = null
      this.searchPerformed = true

      try {
        // Fetch active agencies from our local API
        const apiUrl = this.shopSettings?.apiUrl || 'https://selectair-vakanties.uptodatesites.com'
        const response = await fetch(`${apiUrl}/api/admin/travel-agencies/active`)

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const allAgencies = await response.json()

        // Calculate distance for each agency and filter by range
        const rangeInMeters = parseInt(this.searchRange)
        const agenciesWithDistance = allAgencies
          .filter(agency => agency.coordinates?.latitude && agency.coordinates?.longitude)
          .map(agency => ({
            ...agency,
            distance: this.calculateDistance(
              this.userLocation.latitude,
              this.userLocation.longitude,
              agency.coordinates.latitude,
              agency.coordinates.longitude
            )
          }))
          .filter(agency => agency.distance <= rangeInMeters)
          .sort((a, b) => a.distance - b.distance)

        this.agents = agenciesWithDistance

        // Initialize or update map when agents are loaded
        if (this.viewMode === 'map') {
          this.$nextTick(() => {
            this.initializeMap()
          })
        }
      } catch (error) {
        console.error('Search error:', error)
        this.error = 'Er is een fout opgetreden bij het zoeken naar reisagenten. Probeer het later opnieuw.'
        this.agents = []
      } finally {
        this.loadingAgents = false
      }
    },

    // Haversine formula to calculate distance between two coordinates in meters
    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371000 // Earth's radius in meters
      const dLat = this.toRad(lat2 - lat1)
      const dLon = this.toRad(lon2 - lon1)
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c
    },

    toRad(deg) {
      return deg * (Math.PI / 180)
    },
    
    retrySearch() {
      if (this.userLocation) {
        this.searchAgents()
      } else if (this.zipCode) {
        this.searchByZipCode()
      }
    },
    
    expandSearch() {
      this.searchRange = '100000'
      this.searchAgents()
    },
    
    formatDistance(distanceInMeters) {
      const km = (distanceInMeters / 1000).toFixed(1)
      return `${km} km`
    },
    
    selectAgent(agent) {
      // This could emit an event or navigate to a booking page
      console.log('Selected agent:', agent)
      alert(`U heeft ${agent.name} geselecteerd. Deze functionaliteit wordt nog geïmplementeerd.`)
    },
    
    showOnMap(agent) {
      // Switch to map view and center on the agent
      this.viewMode = 'map'
      this.$nextTick(() => {
        if (this.map) {
          this.centerMapOnAgent(agent)
        } else {
          // Store the agent to center on after map loads
          this.mapCenter = {
            latitude: agent.coordinates.latitude,
            longitude: agent.coordinates.longitude
          }
          this.initializeMap()
        }
      })
    },
    
    handleImageError(event) {
      // Fallback image when agent avatar fails to load
      event.target.src = 'https://uptodatewebdesign.s3.eu-west-3.amazonaws.com/uploads/icon-1741669842731.png'
    },
    
    // Map functionality
    async initializeMap() {
      await this.loadLeaflet()
      
      if (!this.agents.length) return
      
      const mapContainer = document.getElementById('agents-map')
      if (!mapContainer) return
      
      // Clear existing map if it exists
      if (this.map) {
        this.map.remove()
      }
      
      // Calculate center from agents or use user location
      const center = this.mapCenter || this.userLocation || {
        latitude: this.agents[0].coordinates.latitude,
        longitude: this.agents[0].coordinates.longitude
      }
      
      // Initialize map
      this.map = L.map('agents-map').setView([center.latitude, center.longitude], 10)
      
      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(this.map)
      
      // Clear existing markers
      this.markers.forEach(marker => this.map.removeLayer(marker))
      this.markers = []
      
      // Create custom icon for agents
      const agentIcon = L.divIcon({
        className: 'custom-agent-marker',
        html: `
          <div style="
            background-color: #e31c18;
            width: 30px;
            height: 30px;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            border: 3px solid white;
            box-shadow: 0 2px 5px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <div style="
              background-color: white;
              width: 12px;
              height: 12px;
              border-radius: 50%;
              transform: rotate(45deg);
            "></div>
          </div>
        `,
        iconSize: [30, 30],
        iconAnchor: [15, 30]
      })
      
      // Add markers for each agent
      this.agents.forEach(agent => {
        const marker = L.marker([agent.coordinates.latitude, agent.coordinates.longitude], {
          icon: agentIcon
        }).addTo(this.map)
        
        // Create popup content
        const popupContent = `
          <div class="p-3 max-w-xs">
            <h3 class="font-semibold text-gray-900 mb-2">${agent.name}</h3>
            <p class="text-sm text-gray-600 mb-2">${agent.addressLine1}<br>${agent.addressLine2}</p>
            <div class="flex gap-2">
              <button onclick="window.selectAgent('${agent.id}')" class="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors">
                Selecteer
              </button>
              ${agent.website ? `<a href="https://${agent.website}" target="_blank" class="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 transition-colors">Website</a>` : ''}
            </div>
          </div>
        `
        
        marker.bindPopup(popupContent)
        
        // Store reference
        marker.agentId = agent.id
        this.markers.push(marker)
      })
      
      // Add user location marker if available
      if (this.userLocation) {
        const userIcon = L.divIcon({
          className: 'custom-user-marker',
          html: `
            <div style="
              background-color: #4285f4;
              width: 20px;
              height: 20px;
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 2px 5px rgba(0,0,0,0.3);
            "></div>
          `,
          iconSize: [20, 20],
          iconAnchor: [10, 10]
        })
        
        L.marker([this.userLocation.latitude, this.userLocation.longitude], {
          icon: userIcon
        }).addTo(this.map).bindPopup('Uw locatie')
      }
      
      this.mapLoaded = true
    },
    
    loadLeaflet() {
      return new Promise((resolve, reject) => {
        // Check if Leaflet is already loaded
        if (window.L) {
          resolve()
          return
        }
        
        // Load CSS
        const cssLink = document.createElement('link')
        cssLink.rel = 'stylesheet'
        cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
        cssLink.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY='
        cssLink.crossOrigin = ''
        document.head.appendChild(cssLink)
        
        // Load JavaScript
        const script = document.createElement('script')
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
        script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo='
        script.crossOrigin = ''
        script.async = true
        
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('Failed to load Leaflet'))
        
        document.head.appendChild(script)
      })
    },
    
    highlightAgent(agentId) {
      this.highlightedAgent = agentId
      
      // Highlight corresponding marker with Leaflet
      if (this.map && this.markers.length) {
        const marker = this.markers.find(m => m.agentId === agentId)
        if (marker) {
          const highlightIcon = L.divIcon({
            className: 'custom-agent-marker-highlight',
            html: `
              <div style="
                background-color: #c41914;
                width: 36px;
                height: 36px;
                border-radius: 50% 50% 50% 0;
                transform: rotate(-45deg);
                border: 4px solid white;
                box-shadow: 0 3px 8px rgba(0,0,0,0.4);
                display: flex;
                align-items: center;
                justify-content: center;
              ">
                <div style="
                  background-color: white;
                  width: 14px;
                  height: 14px;
                  border-radius: 50%;
                  transform: rotate(45deg);
                "></div>
              </div>
            `,
            iconSize: [36, 36],
            iconAnchor: [18, 36]
          })
          marker.setIcon(highlightIcon)
        }
      }
    },
    
    unhighlightAgent() {
      const previousId = this.highlightedAgent
      this.highlightedAgent = null
      
      // Reset marker icon with Leaflet
      if (this.map && this.markers.length && previousId) {
        const marker = this.markers.find(m => m.agentId === previousId)
        if (marker) {
          const normalIcon = L.divIcon({
            className: 'custom-agent-marker',
            html: `
              <div style="
                background-color: #e31c18;
                width: 30px;
                height: 30px;
                border-radius: 50% 50% 50% 0;
                transform: rotate(-45deg);
                border: 3px solid white;
                box-shadow: 0 2px 5px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
              ">
                <div style="
                  background-color: white;
                  width: 12px;
                  height: 12px;
                  border-radius: 50%;
                  transform: rotate(45deg);
                "></div>
              </div>
            `,
            iconSize: [30, 30],
            iconAnchor: [15, 30]
          })
          marker.setIcon(normalIcon)
        }
      }
    },
    
    centerMapOnAgent(agent) {
      if (this.map) {
        this.map.setView([agent.coordinates.latitude, agent.coordinates.longitude], 15)
        
        // Open the marker's popup
        const marker = this.markers.find(m => m.agentId === agent.id)
        if (marker) {
          marker.openPopup()
        }
      }
    }
  },
  
  watch: {
    viewMode(newMode) {
      if (newMode === 'map' && this.agents.length > 0) {
        this.$nextTick(() => {
          this.initializeMap()
        })
      }
    }
  },
  
  mounted() {
    // Global function for info window buttons
    window.selectAgent = (agentId) => {
      const agent = this.agents.find(a => a.id === agentId)
      if (agent) {
        this.selectAgent(agent)
      }
    }
    
    // Check for auto-search query parameters
    this.checkAutoSearch()
  },
}
</script>

<style>
:root {
  --selectair-text-xs: 12px;
  --selectair-text-sm: 14px;
  --selectair-text-base: 16px;
  --selectair-text-lg: 18px;
  --selectair-text-xl: 20px;
  --selectair-text-2xl: 24px;
  --selectair-line-xs: 16px;
  --selectair-line-sm: 20px;
  --selectair-line-base: 24px;
  --selectair-line-lg: 28px;
  --selectair-line-xl: 28px;
  --selectair-line-2xl: 32px;
}

.agent-finder {
  max-width: 1200px;
  margin: 0 auto;
}

.bg-theme {
  background-color: var(--theme);
}

.bg-theme-hover {
  background-color: var(--theme-hover);
}

.bg-theme-light {
  background-color: #fef2f2;
}

.text-theme {
  color: var(--theme);
}

.hover\:bg-theme-hover:hover {
  background-color: var(--theme-hover);
}

.focus\:ring-theme:focus {
  --tw-ring-color: var(--theme);
}

/* Fix Tailwind text sizes for 10px rem base */
.text-xs {
  font-size: var(--selectair-text-xs) !important;
  line-height: var(--selectair-line-xs) !important;
}

.text-sm {
  font-size: var(--selectair-text-sm) !important;
  line-height: var(--selectair-line-sm) !important;
}

.text-base {
  font-size: var(--selectair-text-base) !important;
  line-height: var(--selectair-line-base) !important;
}

.text-lg {
  font-size: var(--selectair-text-lg) !important;
  line-height: var(--selectair-line-lg) !important;
}

.text-xl {
  font-size: var(--selectair-text-xl) !important;
  line-height: var(--selectair-line-xl) !important;
}

.text-2xl {
  font-size: var(--selectair-text-2xl) !important;
  line-height: var(--selectair-line-2xl) !important;
}

/* Map specific styles */
#agents-map {
  position: relative;
  z-index: 1;
}

/* Custom marker styles */
.custom-agent-marker,
.custom-agent-marker-highlight,
.custom-user-marker {
  background: transparent !important;
  border: none !important;
}

/* Leaflet popup styling */
:global(.leaflet-popup-content-wrapper) {
  border-radius: 8px !important;
  padding: 0 !important;
}

:global(.leaflet-popup-content) {
  margin: 0 !important;
  padding: 0 !important;
}

:global(.leaflet-popup-tip) {
  background: white !important;
}

/* Agent image effects */
.agent-image-container {
  background: linear-gradient(45deg, #f3f4f6, #e5e7eb);
}

.agent-image-blur {
  filter: blur(8px);
  transform: scale(1.1);
  opacity: 0.8;
}

.agent-image-main {
  backdrop-filter: blur(0px);
}

/* Responsive map adjustments */
@media (max-width: 1024px) {
  .map-container {
    height: 80vh !important;
  }
  
  #agents-map {
    min-height: 300px;
  }
}
</style> 